import type {
  AiUsageBreakdownRow,
  AiUsageSummary,
  TelephonyUsageSummary,
} from "@lumika/shared";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MobileList,
  MobileListRow,
  Section,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@lumika/ui";
import { BarChart, BreakdownBars } from "./Charts";

/**
 * Shared billing presentation — the per-dealership and platform-wide
 * pages both feed it an `ai` summary and (optionally) a `twilio` one.
 *
 * This is an analysis view, not a ledger dump: it leads with the spend
 * trend and cost breakdowns, and deliberately HIDES $0 line items —
 * a row that cost nothing carries no analytical signal and only
 * clutters the page. Models that ran with no price configured are not
 * silently dropped: they surface as an explicit data-quality note.
 */
export function BillingView({
  ai,
  twilio,
  totals,
  range,
  unattributedTwilioUsd,
  coverageNote,
}: {
  ai: AiUsageSummary;
  twilio?: TelephonyUsageSummary;
  totals: { aiUsd: number; twilioUsd: number; totalUsd: number };
  range: { start: string; end: string };
  /** Platform-only: Twilio spend on accounts with no dealership association. */
  unattributedTwilioUsd?: number;
  /** Platform-only: shown when synced coverage is narrower than the query range. */
  coverageNote?: string;
}) {
  // Models with real usage but no price match — cost $0, so they are
  // filtered out of the breakdowns below. Surface them once, plainly.
  const unpricedModels = ai.byModel.filter((r) => r.unpriced);

  // Twilio rows are per-day-per-category; roll them up to cost per
  // category so the breakdown answers "where did Twilio spend go".
  const twilioByCategory = new Map<string, number>();
  for (const r of twilio?.rows ?? []) {
    const label = categoryLabel(r.category);
    twilioByCategory.set(label, (twilioByCategory.get(label) ?? 0) + r.priceUsd);
  }

  return (
    <div className="flex flex-col gap-6">
      {coverageNote && (
        <p className="text-sm text-muted-foreground">{coverageNote}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Total cost" value={fmtUsd(totals.totalUsd)} />
        <Metric label="AI cost" value={fmtUsd(totals.aiUsd)} />
        <Metric label="Twilio cost" value={fmtUsd(totals.twilioUsd)} />
        <Metric
          label="AI ledger rows"
          value={ai.totals.rows.toLocaleString("en-US")}
        />
        {unattributedTwilioUsd != null && unattributedTwilioUsd > 0 && (
          <Metric
            label="Unattributed Twilio"
            value={fmtUsd(unattributedTwilioUsd)}
          />
        )}
      </div>

      <Section
        title="Cost by day"
        description={`Daily AI spend across ${range.start} → ${range.end}.`}
      >
        <BarChart
          data={ai.byDay.map((r) => ({ label: shortDay(r.label), value: r.costUsd }))}
          formatValue={fmtUsd}
          emptyText="No AI cost in this range."
        />
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          title="AI cost by provider"
          description="Share of AI spend per provider."
        >
          <BreakdownBars
            data={ai.byProvider.map(toCostDatum)}
            formatValue={fmtUsd}
            emptyText="No billable AI usage."
          />
        </Section>
        <Section
          title="AI cost by kind"
          description="Text generation, transcription, phone calls, images."
        >
          <BreakdownBars
            data={ai.byKind.map(toCostDatum)}
            formatValue={fmtUsd}
            emptyText="No billable AI usage."
          />
        </Section>
      </div>

      <Section
        title="AI by model"
        description="Every model with real spend, highest first. Tokens shown for unit-economics context."
      >
        <ModelTable rows={ai.byModel} />
        {unpricedModels.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            {unpricedModels.length} model
            {unpricedModels.length === 1 ? "" : "s"} ran with no price
            configured and {unpricedModels.length === 1 ? "is" : "are"} not
            counted above: {unpricedModels.map((r) => r.label).join(", ")}.
          </p>
        )}
      </Section>

      {twilio && (
        <Section
          title="Twilio cost by category"
          description="Calls, SMS, MMS, number rental, and A2P brand & campaign fees."
        >
          <BreakdownBars
            data={[...twilioByCategory.entries()].map(([label, value]) => ({
              label,
              value,
            }))}
            formatValue={fmtUsd}
            emptyText="No Twilio cost in this range."
          />
        </Section>
      )}
    </div>
  );
}

/** AI breakdown row → a cost datum for the breakdown charts. */
function toCostDatum(r: AiUsageBreakdownRow): { label: string; value: number } {
  return { label: r.label, value: r.costUsd };
}

/**
 * Model-level detail — the one place a table earns its keep, because
 * there can be many models. Rows that cost $0 are dropped; the rest
 * are ranked by spend with each model's share of the AI total.
 */
function ModelTable({ rows }: { rows: AiUsageBreakdownRow[] }) {
  const billable = rows
    .filter((r) => r.costUsd > 0)
    .sort((a, b) => b.costUsd - a.costUsd);
  if (billable.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No model had billable spend in this range.
      </p>
    );
  }
  const total = billable.reduce((sum, r) => sum + r.costUsd, 0);
  return (
    <>
      <TableBleed className="-mt-6 hidden border-b md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Tokens in → out</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {billable.map((r) => (
              <TableRow key={r.key}>
                <TableCell className="font-medium">{r.label}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {r.inputTokens.toLocaleString("en-US")} →{" "}
                  {r.outputTokens.toLocaleString("en-US")}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtUsd(r.costUsd)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {total > 0 ? `${Math.round((r.costUsd / total) * 100)}%` : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableBleed>
      <MobileList>
        {billable.map((r) => (
          <MobileListRow
            key={r.key}
            primary={r.label}
            secondary={`${r.inputTokens.toLocaleString("en-US")} → ${r.outputTokens.toLocaleString("en-US")} tokens`}
            meta={
              total > 0 ? (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {Math.round((r.costUsd / total) * 100)}% of spend
                </span>
              ) : undefined
            }
            trailing={
              <span className="tabular-nums font-medium">{fmtUsd(r.costUsd)}</span>
            }
          />
        ))}
      </MobileList>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtUsd(n: number): string {
  return usd.format(n);
}

/** YYYY-MM-DD → MM-DD, the compact label the day chart wants. */
function shortDay(day: string): string {
  return day.length >= 10 ? day.slice(5) : day;
}

/**
 * Twilio's raw usage-record category codes are cryptic (e.g.
 * `a2p-10dlc-registrationfees-brandregistration`). Map the A2P 10DLC
 * onboarding charges to readable labels so brand / campaign / vetting
 * spend is legible at a glance; every other category falls through to
 * its raw code unchanged.
 */
function categoryLabel(category: string): string {
  const c = category.toLowerCase();
  if (c.includes("a2p") || c.includes("registrationfees")) {
    if (c.includes("vetting")) return "A2P brand vetting";
    if (c.includes("brand")) return "A2P brand registration";
    if (c.includes("campaign")) return "A2P campaign fee";
    return "A2P 10DLC fee";
  }
  return category;
}

/** Default 30-day range used by both pages. Exported so the parent can
 *  use the same fallback when calling the API. Built from local date
 *  parts (not toISOString) so an evening admin's "today" isn't UTC's
 *  tomorrow. */
export function defaultBillingRange(): { start: string; end: string } {
  const today = new Date();
  return {
    start: toLocalDay(new Date(today.getTime() - 30 * 86_400_000)),
    end: toLocalDay(today),
  };
}

function toLocalDay(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
